/**
 * StaffMasthead.tsx — the staff top bar's WHITE HEADER BLOCK (UX Wave 7B).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 7B (shell polish
 * to analytics-panel parity, 2026-07-21 late).
 *
 * WHY: the CTO's Wave-7B pass mirrors the analytics admin webapp's header —
 * the GPS logo moves OFF the green rail onto the white top bar, with the
 * workspace name sitting NEXT TO the logo behind a thin vertical rule (the
 * "Novamedica │ Analytics" pattern), replacing the old in-content
 * "Admin console"/"Clinician workspace" page-title block. The workspace name
 * stays the page's ONE `<h1>` (compact, body-face) so the document outline —
 * and every existing "the console rendered" test beacon — is unchanged.
 * UNDER the logo row sits the BREADCRUMB ("Admin console › Payments"),
 * updating per section; the root crumb is plain text (the workspace has no
 * page of its own — sections are `?tab=` states) and the current section
 * carries `aria-current="page"`.
 *
 * The logo keeps its home-link behaviour (the same aria-label the rail link
 * carried) and renders through BrandLockup's `ground="light"` variant — the
 * white PNG is used as a CSS mask over brand green, so the mark stays legible
 * on the white block (see ui/kit.tsx).
 *
 * R28: category-level copy only — workspace/section names, never a POM name.
 */
import { Link } from 'react-router-dom';
import { BrandLockup, ChevronRightIcon } from './kit';

export function StaffMasthead({
  workspace,
  section,
  homeTo,
  homeLabel,
}: {
  /** The workspace name next to the logo — "Admin console" / "Clinician workspace". */
  workspace: string;
  /** The active section's label — the breadcrumb's current crumb. */
  section: string;
  /** The logo's home-link target (`/admin` or `/clinicians`). */
  homeTo: string;
  /** The home link's accessible name (e.g. "GP Service — admin home"). */
  homeLabel: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)' }}>
        <Link to={homeTo} aria-label={homeLabel} style={{ display: 'inline-flex', textDecoration: 'none' }}>
          <BrandLockup ground="light" />
        </Link>
        {/* The thin vertical rule + workspace name — the analytics
            product-name treatment. The name is the page's single h1. */}
        <span
          aria-hidden="true"
          style={{ width: '1px', alignSelf: 'stretch', margin: '2px 0', background: 'var(--gps-border)', flex: 'none' }}
        />
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--gps-font-body)',
            fontSize: '16px',
            fontWeight: 600,
            letterSpacing: '0.01em',
            color: 'var(--gps-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {workspace}
        </h1>
      </div>

      {/* The per-section breadcrumb, UNDER the logo (Wave 7B §8). */}
      <nav aria-label="Breadcrumb" style={{ fontSize: '12px', color: 'var(--gps-muted)' }}>
        <ol style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: '6px', margin: 0, padding: 0 }}>
          <li>{workspace}</li>
          <li aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <ChevronRightIcon size={12} />
          </li>
          <li aria-current="page" style={{ color: 'var(--gps-text)', fontWeight: 600 }}>
            {section}
          </li>
        </ol>
      </nav>
    </div>
  );
}
